import React, { useCallback, useRef } from 'react';
import { ReactComponent as More } from 'assets/icons/more-options.svg';
import useOutsideClick from '../datePicker/hooks/useOutsideClick';
import ButtonComponent from '../button/buttonComponent';
import { twMerge } from 'tailwind-merge';

type OptionDivider = {
  divider?: boolean;
};

type OptionItem = {
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  label: string;
  iconClassName?: string;
  tooltipId?: string;
  className?: string;
  disabled?: boolean;
  onClick?: () => void;
  hidden?: boolean;
};

export type IOptionProps = OptionItem | OptionDivider;

interface IOptionModalProps {
  opened: boolean;
  setOpened: React.Dispatch<React.SetStateAction<boolean>>;
  className?: string;
  openedClassName?: string;
  fill?: string;
  optionsClassName?: string;
  btnTitleClassName?: string;
  optionsMenuClassName?: string;
  iconClassName?: string;
  options: IOptionProps[];
  children: React.ReactNode;
}

export const OptionsModalContainer: React.FC<IOptionModalProps> = ({
  opened,
  setOpened,
  options,
  optionsMenuClassName,
  optionsClassName,
  children,
}) => {
  const optionsRef = useRef<HTMLDivElement>(null);
  const closeOptions = useCallback(() => setOpened(false), [setOpened]);

  useOutsideClick(optionsRef, closeOptions, opened);
  return (
    <div className="relative inline-block" ref={optionsRef}>
      {children}
      {opened && (
        <div
          className={twMerge(
            'absolute z-50 w-[200px] bg-white transform -translate-y-[1px] rounded-md rounded-tl-[0px] border border-med-gray-3 overflow-hidden',
            optionsMenuClassName,
          )}
          data-testid="options-menu"
        >
          {!!options.length &&
            options.map((option) => {
              if ((option as OptionDivider)?.divider) {
                return <hr className="mx-5 my-2.5" />;
              }

              const {
                Icon,
                label,
                className,
                onClick,
                disabled,
                iconClassName,
                hidden,
                tooltipId,
              } = option as OptionItem;
              const handleClick = (
                event: React.MouseEvent<HTMLButtonElement>,
              ) => {
                event.preventDefault();
                event.stopPropagation();
                onClick && onClick();
              };
              return (
                <button
                  key={label}
                  onClick={handleClick}
                  disabled={disabled}
                  className={twMerge(
                    'p-3 flex items-center w-full bg-white hover:bg-hover-blue',
                    disabled && 'opacity-50 cursor-not-allowed',
                    hidden && 'hidden',
                    className,
                  )}
                  data-tooltip-id={tooltipId}
                  data-testid={'button-' + label}
                >
                  {!!Icon && (
                    <span className="mr-2">
                      <Icon className={twMerge('w-6', iconClassName)} />
                    </span>
                  )}
                  <span
                    className={twMerge(
                      'text-base font-medium text-med-gray-3',
                      optionsClassName,
                    )}
                  >
                    {label}
                  </span>
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
};

const OptionsModal: React.FC<Omit<IOptionModalProps, 'children'>> = ({
  opened,
  setOpened,
  options,
  className,
  openedClassName,
  fill,
  btnTitleClassName,
  optionsMenuClassName,
  optionsClassName,
}) => {
  const handleOptionsClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setOpened((opened) => !opened);
    },
    [setOpened],
  );

  return (
    <OptionsModalContainer
      className={className}
      opened={opened}
      setOpened={setOpened}
      options={options}
      openedClassName={openedClassName}
      optionsClassName={optionsClassName}
      optionsMenuClassName={optionsMenuClassName}
    >
      <ButtonComponent
        Icon={More}
        type="outlined"
        testID={'more'}
        className={twMerge(
          'desktop:px-1.5 group font-bold text-med-gray-3 hover:text-clc-blue active:text-clc-blue border-med-gray-3 border hover:bg-white active:bg-white',
          opened &&
            'border-b-transparent text-clc-blue rounded-b-[0px] z-10 relative',
          className,
          opened && openedClassName,
        )}
        fill={
          fill ||
          (opened
            ? 'fill-clc-blue'
            : 'fill-med-gray-3 group-hover:fill-clc-blue')
        }
        stroke="stroke-transparent"
        iconPosition="left"
        onClick={handleOptionsClick}
      >
        <span className={twMerge('desktop:hidden', btnTitleClassName)}>
          More
        </span>
      </ButtonComponent>
    </OptionsModalContainer>
  );
};

export default OptionsModal;
